import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import ThemeSettingsSection, { useStoreThemeForm } from './ThemeSettingsSection';
import UnsavedChangesBar from './UnsavedChangesBar';
import { SettingsSheet, SheetBlock, SheetSection } from './sheet';
import { StoreLoadState, StoreRow, useStoreSettingsForm } from './storeSettingsForm';

/**
 * Κατάστημα → «Εμφάνιση» (12/10/2026, από την παλιά SettingsPage): το θέμα (χρώμα, φόντο, ανοιχτό/σκούρο —
 * PUT /api/stores/:id/theme) και η γλώσσα (PATCH /api/stores/:id, μόνο ό,τι άλλαξε).
 * Από 13/10/2026 (σχέδιο A «Φύλλο») ΜΙΑ μπάρα «Αποθήκευση» για όλα: ως τότε το θέμα είχε δικό του κουμπί
 * και η μπάρα της σελίδας δεν το έβλεπε.
 * Η γλώσσα αλλάζει αμέσως σε αυτή τη συσκευή (προεπισκόπηση) και αποθηκεύεται με την «Αποθήκευση»·
 * «Αναίρεση» ή έξοδος χωρίς αποθήκευση γυρίζουν την οθόνη στην αποθηκευμένη γλώσσα.
 * Η οθόνη πελάτη ΔΕΝ είναι πια εδώ (ιδιοκτήτης 25/10/2026: «δεν ταιριάζει»): Ταμείο → «Συσκευές ταμείου».
 */

type Language = 'el' | 'en';

interface AppearanceForm {
  language: Language;
}

const EMPTY: AppearanceForm = { language: 'el' };

const fromStore = (s: StoreRow): AppearanceForm => ({
  language: s.language === 'en' ? 'en' : 'el',
});

const LANGUAGES: Array<{ id: Language; flag: string; labelKey: string }> = [
  { id: 'el', flag: '🇬🇷', labelKey: 'common:greek' },
  { id: 'en', flag: '🇬🇧', labelKey: 'common:english' },
];

const AppearancePage: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const { form, status, retry, saving, save } = useStoreSettingsForm({ guardId: 'store-settings:appearance', empty: EMPTY, fromStore });
  const theme = useStoreThemeForm(store, 'store-settings:appearance-theme');
  const [themeInvalid, setThemeInvalid] = useState(false);
  const { values: v, setField } = form;
  const busy = saving || theme.saving;
  const themeChanged = theme.form.changedKeys;

  const applyLanguage = (lang: Language) => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    localStorage.setItem('store_language', lang);
  };

  // Έξοδος χωρίς αποθήκευση: πίσω στη γλώσσα που είναι αποθηκευμένη.
  const languageRef = useRef({ shown: v.language, saved: form.saved.language, loaded: false });
  useEffect(() => {
    languageRef.current = { shown: v.language, saved: form.saved.language, loaded: status === 'ready' };
  });
  useEffect(() => () => {
    const { shown, saved, loaded } = languageRef.current;
    if (loaded && shown !== saved) applyLanguage(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseLanguage = (lang: Language) => {
    setField('language', lang);
    applyLanguage(lang);
  };

  // Θέμα και στοιχεία καταστήματος είναι δύο αιτήματα: όποιο αποτύχει δείχνει το δικό του μήνυμα και
  // μένει με τη μπάρα· ένα «αποθηκεύτηκαν» μόνο όταν πέτυχαν όλα.
  const onSave = async () => {
    if (busy || themeInvalid) return;
    let ok = true;
    if (theme.form.dirty) {
      try {
        await theme.save();
      } catch (err: any) {
        ok = false;
        showToast('error', err?.message || t('settings:theme.saveError'));
      }
    }
    if (form.dirty) ok = (await save({ successToast: false })) && ok;
    if (ok) showToast('success', t('settings:page.saveSuccess'));
  };

  const onReset = () => {
    if (v.language !== form.saved.language) applyLanguage(form.saved.language);
    form.reset();
    theme.form.reset();
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.store')} title={t('settings:appearance.title')} subtitle={t('settings:appearance.subtitle')}>
      <fieldset disabled={busy} className="min-w-0">
        {store?.id ? (
          <ThemeSettingsSection
            variant="sheet"
            scope="store"
            inherited={theme.org}
            hasOrg={store.organization_id != null}
            canEdit={theme.canEdit}
            loading={theme.loading}
            storeName={store.store_name || store.name}
            draft={theme.form.values}
            onDraftChange={patch => theme.form.setValues(prev => ({ ...prev, ...patch }))}
            onInvalidChange={setThemeInvalid}
            changed={{
              brand: themeChanged.includes('brand'),
              surface: themeChanged.includes('surface'),
              appearance: themeChanged.includes('appearance'),
            }}
            resetKey={theme.form.version}
          />
        ) : null}

        {status !== 'ready' ? (
          <SheetBlock><StoreLoadState status={status} onRetry={retry} /></SheetBlock>
        ) : (
          <>
            <SheetSection id="appearance-language" title={t('settings:language')} hint={t('settings:languageDescription')}
              dirty={form.changedKeys.includes('language')}>
              <div className="flex gap-2 max-w-[22.5rem]" role="radiogroup" aria-label={t('settings:language')}>
                {LANGUAGES.map(lang => {
                  const checked = v.language === lang.id;
                  return (
                    <button
                      key={lang.id}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      onClick={() => chooseLanguage(lang.id)}
                      className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl border text-sm font-medium transition-colors ${
                        checked ? 'border-brand bg-brand-soft text-brand font-semibold' : 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3'
                      }`}
                    >
                      <span className="text-lg leading-none" aria-hidden="true">{lang.flag}</span>
                      {t(lang.labelKey)}
                    </button>
                  );
                })}
              </div>
              <p className="xs-note">{t('settings:appearance.languageHint')}</p>
            </SheetSection>
          </>
        )}
      </fieldset>

      <UnsavedChangesBar dirty={form.dirty || theme.form.dirty} saving={busy} canSave={!themeInvalid} onSave={onSave} onReset={onReset} />
    </SettingsSheet>
  );
};

export default AppearancePage;
